import {
  Component,
  OnInit,
  Input,
  Optional,
  Host,
  SkipSelf,
  HostBinding,
  ElementRef,
  Renderer2,
  OnDestroy,
  ChangeDetectorRef,
  EventEmitter,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
  NgZone,
} from '@angular/core';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/combineLatest';
import { Subscription } from 'rxjs/Subscription';
import { LookupService } from '../services/LookupService';
import { LookupModel } from '../../common/models/LookupModel';
import { MatFormFieldControl, MatAutocomplete } from '@angular/material';
import { NgControl, ControlValueAccessor, NG_VALUE_ACCESSOR, AbstractControl, ControlContainer, Validator } from '@angular/forms';
import { Subject } from 'rxjs/Subject';
import { FocusMonitor } from '@angular/cdk/a11y';
import { BehaviorSubject } from 'rxjs';
import { IMetadataWorkspaceContext } from '../../pillar/workspace/models/IMetadataWorkspaceContext';
import { IMetadataEvent } from '../../pillar/workspace/models/IMetadataEvent';

@Component({
  selector: 'brd-lookup-input',
  templateUrl: './lookup-input.component.html',
  styleUrls: ['./lookup-input.component.scss'],
  inputs: ['category', 'placeholder', 'formControlName', 'ariaLabel'],
  providers: [{ provide: MatFormFieldControl, useExisting: LookupInputComponent }],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LookupInputComponent implements OnInit, OnDestroy, MatFormFieldControl<LookupModel>, ControlValueAccessor {
  shouldPlaceholderFloat?: boolean;

  stateChanges = new Subject<void>();
  destroyed$ = new Subject<boolean>();
  focused: boolean;
  loadedValues: boolean;
  disabled: boolean;
  controlType?: string;
  options: LookupModel[];
  hasOptions = false;
  options$: BehaviorSubject<LookupModel[]> = new BehaviorSubject<LookupModel[]>([]);
  static nextId = 0;
  public lookupControl: AbstractControl;
  propagateChange: any = () => {};
  touched: any = () => {};
  currentPage = -1;
  hasMore = true;
  isLoading = false;
  _context: IMetadataWorkspaceContext;
  selectedCodes: string[] = [];

  @Input()
  pageSize = 200;

  @Input()
  jurisdictionalDomain: string;

  @Input()
  category: string;

  @Input()
  termType: string;

  @Input()
  showCode: boolean;

  @Input()
  formControlName: string;

  @Input()
  placeholder: string;

  @Input()
  required: boolean;

  @Input()
  ariaLabel: string;
  
  @Input()
  set context(context: IMetadataWorkspaceContext) {
    if (this._context != context) {
      this._context = context;
    }
  }

  @Input('options')
  set passedInOptions(options: LookupModel[]) {
    this.options = options;
    this.updateOptions();
  }

  @Input()
  get value(): any | null {
    return this.empty ? null : this.elementRef.nativeElement.value;
  }
  set value(lookupModel: any | null) {
    this.writeValue(lookupModel);
    this.stateChanges.next();
  }

  @Output()
  selected = new EventEmitter<LookupModel>();

  get errorState() {
    return this.ngControl ? this.ngControl.invalid : false;
  }

  get empty() {
    return this.ngControl == null || this.ngControl.value == null || this.ngControl.value == undefined || this.ngControl.value === '' || this.ngControl.value.length === 0;
  }
  @HostBinding() id = `brd-lookup-input-${LookupInputComponent.nextId++}`;
  @HostBinding('class.floating') get shouldLabelFloat() {
    return this.focused || !this.empty;
  }

  @ViewChild(MatAutocomplete)
  matAutocomplete: MatAutocomplete;

  constructor(
    private lookupService: LookupService,
    @Optional()
    @Host()
    @SkipSelf()
    private controlContainer: ControlContainer,
    @Optional() public ngControl: NgControl,
    private focusMonitor: FocusMonitor,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private changeDetectorRef: ChangeDetectorRef,
    private _ngZone: NgZone,
  ) {
    focusMonitor
      .monitor(elementRef.nativeElement, true)
      .takeUntil(this.destroyed$)
      .subscribe(origin => {
        this.focused = !!origin;
        if (this.focused && this.ngControl) {
          this.touched();
          this.updateOptions();
          this.collectSelectedCode();
          this.currentPage = -1;
          this.hasMore = true;
          this.loadPage();
        }
        this.stateChanges.next();
      });
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  validLookup(control: AbstractControl) {
    if (control.value != null && (control.value.key === null || control.value.key === undefined || control.value.key === '')) {
      return { validLookup: { value: control.value } };
    }
    return null;
  }

  ngOnInit() {
    if (this.controlContainer) {
      if (this.formControlName) {
        this.lookupControl = this.controlContainer.control.get(this.formControlName);
        this.lookupControl.setValidators([this.validLookup]);
      } else {
        console.warn('Missing FormControlName directive from host element of the component');
      }
    } else {
      console.warn("Can't find parent FormGroup directive");
    }

    if (this.ngControl) {
      this.ngControl.valueChanges
        .takeUntil(this.destroyed$)
        .debounceTime(500)
        .subscribe(() => {
          this.currentPage = -1;
          this.hasMore = true;
          this.options = undefined;
          this.updateOptions();
          this.loadPage();
        });
    }
    this.lookupControl.valueChanges
      .takeUntil(this.destroyed$)
      .subscribe(
      (value: LookupModel) => this.selected.emit(value)
    );

    if (!(this.category || this.jurisdictionalDomain)) {
      this.hasMore = false;
    }
  }

  loadPage() {
    if (this.hasMore) {
      this.setLoading();
      this.currentPage++;
      var value = this.ngControl ? this.ngControl.value : null;
      var valid = this.ngControl ? this.ngControl.value : true;
      var searchTerm = valid && Object.prototype.toString.call(value) === '[object String]' ? value : null;
      if (this.category) {
        if (value != null && this.selectedCodes.includes(value.key)) {
          this.removeItem(this.selectedCodes, value.key);
        }
        this.lookupService
          .getLooups(this.category, this.termType, this.currentPage, this.pageSize, null, null, searchTerm, this.selectedCodes)
          .take(1)
          .subscribe(lookups => {
            this.options = this.filterExistingUniqueOptions(lookups);
            this.hasMore = lookups.length === this.pageSize;
            this.doneLoading();
            this.updateOptions();
          });
      } else if (this.jurisdictionalDomain) {
        this.lookupService
          .getValueSets(this.jurisdictionalDomain, this.currentPage, this.pageSize, searchTerm)
          .take(1)
          .subscribe(lookups => {
            this.options = this.filterExistingUniqueOptions(lookups);
            this.hasMore = lookups.length === this.pageSize;
            this.doneLoading();
            this.updateOptions();
          });
      } else {
        this.doneLoading();
      }
    }
  }

  private filterExistingUniqueOptions(options: LookupModel[]) {
    var control = this.controlContainer.control as any;
    var listPropertyName = control.nameIdentifier;
    var selectedOptions = [];
    var propertyControl = null;
    if (this._context != null) {
      propertyControl = this._context.formGroup.controls[listPropertyName];
    }
    if (propertyControl != null && propertyControl.value instanceof Array) {
      propertyControl.value.forEach(so => {
        if (so[this.formControlName] != null) {
          selectedOptions.push(so[this.formControlName]);
        }
      });
    }
    return options.filter(o => o.isUniquePerParent == null || o.isUniquePerParent === false || o.isUniquePerParent && !selectedOptions.some(so => so.key === o.key && so.description === o.description));
  }

  private collectSelectedCode() {
    var selectedCodes = [];
    var customizeFields = Object.keys(this.controlContainer.value);
    customizeFields.forEach(f => {
      var model = this.controlContainer.value[f];
      if (model != null && model.key != null) {
        selectedCodes.push(model.key);
      }
    });
    this.selectedCodes = selectedCodes;
  }

  private setLoading() {
    this.isLoading = true;
  }

  private doneLoading() {
    this.isLoading = false;
  }

  private updateOptions(): void {
    this._ngZone.run(() => {
      if (this.options) {
        this.options$.next(this.options);
        this.hasOptions = true;
      }
      this.changeDetectorRef.markForCheck();
    });
  }

  displayFn(lookupModel?: LookupModel): string | undefined {
    if (lookupModel && lookupModel.key) {
      if (this.showCode) {
        return '(' + lookupModel.key.substr(lookupModel.key.lastIndexOf('.') + 1) + ') ' + lookupModel.description;
      }
      return lookupModel.description;
    }
    return undefined;
  }

  trackOption(index: number, item: LookupModel) {
    if (!item) {
      return item;
    }
    return item.key;
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  writeValue(obj: any): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'value', obj);
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.touched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  setDescribedByIds(ids: string[]): void {}

  onContainerClick(event: MouseEvent): void { }

  removeItem (arr, value) {
    var index = arr.indexOf(value);
    if (index > -1) {
      arr.splice(index, 1);
    }
    return arr;
  }
}



// WEBPACK FOOTER //
// ./src/app/core/components/lookup-input.component.ts